import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import {
  jobEndpoints,
  membershipEndpoints,
  officeRecoveryRenewalEndpoints,
  recurringBillingEndpoints,
  recurringJobEndpoints,
} from '@/lib/api/endpoints';
import { officeRecoveryRenewalKeys } from '@/lib/queryKeys';
import type { RecurringBillingRepairLinkResponse } from '@/types/recurring-billing';

export type OfficeQueueItemType = 'JOB' | 'MEMBERSHIP';

export interface OfficeQueueContact {
  id: string;
  displayName: string | null;
  companyName: string | null;
  email: string | null;
}

export interface OfficeRecoveryQueueItem {
  type: OfficeQueueItemType;
  id: string;
  label: string;
  number: string | null;
  status: string;
  contact: OfficeQueueContact | null;
  pastDueAt: string | null;
  suspendedAt: string | null;
  amountAtRisk?: number | null;
  plan: {
    id?: string;
    status?: string;
    failureCount?: number | null;
    lastErrorCode?: string | null;
    lastErrorMessage?: string | null;
    repairRequestedAt?: string | null;
    repairSentAt?: string | null;
    repairExpiresAt?: string | null;
    failedPaymentCount?: number | null;
    paymentRecoveryStage?: number | null;
    nextBillingDate?: string | null;
    providerSubscriptionId?: string | null;
  } | null;
}

export interface OfficeRenewalQueueItem {
  type: OfficeQueueItemType;
  id: string;
  label: string;
  number: string | null;
  status: string;
  contact: OfficeQueueContact | null;
  autoRenew: boolean;
  endDate: string | null;
  renewalNoticeSentAt: string | null;
  successorStatus?: 'DRAFTED' | 'ACTIVE' | null;
}

export interface OfficeQueueResponse<T> {
  data: {
    items: T[];
    total: number;
    page?: number;
    limit?: number;
  };
}

export type OfficeQueueParams = {
  page: number;
  limit: number;
  q?: string;
  type?: string;
  status?: string;
  renewalFilter?: string;
};

function queuePath(path: string, params: OfficeQueueParams) {
  const search = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
  });
  const q = params.q?.trim();
  if (q) search.set('q', q);
  if (params.type && params.type !== 'ALL') search.set('type', params.type);
  if (params.status && params.status !== 'ALL') search.set('status', params.status);
  if (params.renewalFilter && params.renewalFilter !== 'ALL') {
    search.set('renewalFilter', params.renewalFilter);
  }
  return `${path}?${search.toString()}`;
}

export function useOfficeRecoveryQueue(params: OfficeQueueParams, options?: { enabled?: boolean }) {
  return useApiQuery<OfficeQueueResponse<OfficeRecoveryQueueItem>>({
    queryKey: officeRecoveryRenewalKeys.recovery(params),
    queryFn: () =>
      apiGet<OfficeQueueResponse<OfficeRecoveryQueueItem>>(
        operationsClient,
        queuePath(officeRecoveryRenewalEndpoints.recoveryQueue(), params),
      ),
    showErrorToast: false,
    enabled: options?.enabled,
  });
}

export function useOfficeRenewalQueue(params: OfficeQueueParams, options?: { enabled?: boolean }) {
  return useApiQuery<OfficeQueueResponse<OfficeRenewalQueueItem>>({
    queryKey: officeRecoveryRenewalKeys.renewals(params),
    queryFn: () =>
      apiGet<OfficeQueueResponse<OfficeRenewalQueueItem>>(
        operationsClient,
        queuePath(officeRecoveryRenewalEndpoints.renewalQueue(), params),
      ),
    showErrorToast: false,
    enabled: options?.enabled,
  });
}

export function useResendRecoveryRepairLink() {
  return useApiMutation<RecurringBillingRepairLinkResponse, { planId: string }>({
    mutationFn: ({ planId }) =>
      apiPost<RecurringBillingRepairLinkResponse, Record<string, never>>(
        operationsClient,
        recurringBillingEndpoints.createRepairLink(planId),
        {},
      ),
  });
}

export function useRetryRecoveryCharge() {
  return useApiMutation<unknown, { planId: string }>({
    mutationFn: ({ planId }) =>
      apiPost(operationsClient, recurringBillingEndpoints.retryPlanFailure(planId), {}),
  });
}

export function useRetryMembershipPayment() {
  return useApiMutation<unknown, { id: string }>({
    mutationFn: ({ id }) => apiPost(operationsClient, membershipEndpoints.retryPayment(id), {}),
  });
}

export function useRenewOfficeItem() {
  return useApiMutation<unknown, { type: OfficeQueueItemType; id: string }>({
    mutationFn: ({ type, id }) =>
      apiPost(
        operationsClient,
        type === 'JOB' ? recurringJobEndpoints.renew(id) : membershipEndpoints.renewContract(id),
        {},
      ),
  });
}

export function useTurnOffOfficeAutoRenew() {
  return useApiMutation<unknown, { type: OfficeQueueItemType; id: string }>({
    mutationFn: ({ type, id }) =>
      type === 'JOB'
        ? apiPatch(operationsClient, jobEndpoints.update(id), { autoRenew: false })
        : apiPatch(operationsClient, membershipEndpoints.update(id), { autoRenew: false }),
  });
}

export function useReactivateOfficeMembership() {
  return useApiMutation<unknown, { id: string }>({
    mutationFn: ({ id }) => apiPost(operationsClient, membershipEndpoints.reactivate(id), {}),
  });
}

export function useSuspendOfficeMembership() {
  return useApiMutation<unknown, { id: string }>({
    mutationFn: ({ id }) => apiPost(operationsClient, membershipEndpoints.suspend(id), {}),
  });
}
